import queryClient from '@src/app/providers/queryClient';
import type {
  QualificationOptionDto,
  UserQualificationDto,
  UserQualificationStateCommitPayload,
  UserQualificationsResponseDto,
  UserQualificationWritePayload,
} from '@src/features/users/types';
import { useMutation, useQuery } from '@src/shared/query';
import { urlToQueryKey } from '@src/shared/query/utils';

function qualificationsUrl(userId: number | string) {
  return `users/${String(userId)}/qualifications/`;
}

export function invalidateUserQualificationsQuery(userId: number | string) {
  return queryClient.invalidateQueries({
    queryKey: ['root', ...urlToQueryKey(qualificationsUrl(userId))],
  });
}

export function useUserQualifications(
  userId: number | string | null | undefined,
) {
  return useQuery<UserQualificationsResponseDto>(
    qualificationsUrl(userId ?? ''),
    { enabled: Boolean(userId) },
  );
}

export function useQualificationOptions(
  userId: number | string | null | undefined,
  enabled = true,
) {
  return useQuery<QualificationOptionDto[]>(
    `${qualificationsUrl(userId ?? '')}options/`,
    { enabled: Boolean(userId) && enabled },
  );
}

export function useCreateUserQualification() {
  return useMutation<
    UserQualificationDto,
    Error,
    {
      userId: number | string;
      payload: UserQualificationWritePayload;
    }
  >({
    mutationKey: ['root', 'users', 'qualifications', 'create'],
    mutateFn: ({ apiClient, userId, payload }) =>
      apiClient.request({
        url: qualificationsUrl(userId),
        method: 'POST',
        payload,
      }) as Promise<UserQualificationDto>,
    onSuccess: (_response, variables) =>
      invalidateUserQualificationsQuery(variables.userId),
  });
}

export function useUpdateUserQualification() {
  return useMutation<
    UserQualificationDto,
    Error,
    {
      userId: number | string;
      assignmentId: number;
      payload: UserQualificationWritePayload & { version: number };
    }
  >({
    mutationKey: ['root', 'users', 'qualifications', 'update'],
    mutateFn: ({ apiClient, userId, assignmentId, payload }) =>
      apiClient.request({
        url: `${qualificationsUrl(userId)}${String(assignmentId)}/`,
        method: 'PATCH',
        payload,
      }) as Promise<UserQualificationDto>,
    onSuccess: (_response, variables) =>
      invalidateUserQualificationsQuery(variables.userId),
  });
}

export function useCommitUserQualifications() {
  return useMutation<
    { results: UserQualificationDto[] },
    Error,
    {
      userId: number | string;
      payload: UserQualificationStateCommitPayload;
    }
  >({
    mutationKey: ['root', 'users', 'qualifications', 'commit'],
    mutateFn: ({ apiClient, userId, payload }) =>
      apiClient.request({
        url: `${qualificationsUrl(userId)}commit/`,
        method: 'POST',
        payload,
      }) as Promise<{ results: UserQualificationDto[] }>,
    onSuccess: (_response, variables) =>
      invalidateUserQualificationsQuery(variables.userId),
  });
}

export function useEndUserQualification() {
  return useMutation<
    UserQualificationDto,
    Error,
    {
      userId: number | string;
      assignmentId: number;
      version: number;
      end: string;
    }
  >({
    mutationKey: ['root', 'users', 'qualifications', 'end'],
    mutateFn: ({ apiClient, userId, assignmentId, version, end }) =>
      apiClient.request({
        url: `${qualificationsUrl(userId)}${String(assignmentId)}/`,
        method: 'PATCH',
        payload: { version, usqu_end: end },
      }) as Promise<UserQualificationDto>,
    onSuccess: (_response, variables) =>
      invalidateUserQualificationsQuery(variables.userId),
  });
}

export function useApproveUserQualification() {
  return useMutation<
    UserQualificationDto,
    Error,
    {
      userId: number | string;
      assignmentId: number;
      version: number;
    }
  >({
    mutationKey: ['root', 'users', 'qualifications', 'approve'],
    mutateFn: ({ apiClient, userId, assignmentId, version }) =>
      apiClient.request({
        url: `${qualificationsUrl(userId)}${String(assignmentId)}/approve/`,
        method: 'POST',
        payload: { version },
      }) as Promise<UserQualificationDto>,
    onSuccess: (_response, variables) =>
      invalidateUserQualificationsQuery(variables.userId),
  });
}
